import type React from "react";
import { useState } from "react";
import { signIn } from "../../lib/auth-client";

type Mode = "password" | "magic-link";

export const LoginForm: React.FC = () => {
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setNotice("");

    if (mode === "password") {
      const { error: err } = await signIn.email({ email, password });
      if (err) {
        setError(
          err.status === 401 || err.status === 403
            ? "Credenciales inválidas."
            : "Error de conexión. Probá de nuevo.",
        );
        setLoading(false);
      }
      // Sin error: useSession() se actualiza solo y App muestra el dashboard.
      return;
    }

    const { error: err } = await signIn.magicLink({
      email,
      callbackURL: window.location.origin,
    });
    if (err) {
      setError("No se pudo enviar el enlace. Verificá el email o probá más tarde.");
    } else {
      setNotice("Listo. Revisá tu correo: el enlace de acceso expira en 10 minutos.");
    }
    setLoading(false);
  };

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit} className="login-form">
        <span className="login-logo">◈ Tagliapietra Portal</span>
        <h2>Acceso BI</h2>
        <p className="login-sub">Gestionado por Tagliapietra</p>

        <div className="login-modes">
          <button
            type="button"
            className={`login-mode ${mode === "password" ? "active" : ""}`}
            onClick={() => setMode("password")}
          >
            Contraseña
          </button>
          <button
            type="button"
            className={`login-mode ${mode === "magic-link" ? "active" : ""}`}
            onClick={() => setMode("magic-link")}
          >
            Enlace por email
          </button>
        </div>

        {error && <div className="login-msg error">{error}</div>}
        {notice && <div className="login-msg notice">{notice}</div>}

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="usuario@empresa.com"
            autoComplete="email"
          />
        </div>

        {mode === "password" && (
          <div className="form-group">
            <label htmlFor="password">Contraseña</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
        )}

        <button type="submit" className="login-submit" disabled={loading}>
          {loading ? "Procesando..." : mode === "password" ? "Iniciar sesión" : "Enviar enlace"}
        </button>
      </form>

      <style>{`
        .login-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          background: var(--color-bg);
          font-family: var(--font-sans);
        }
        .login-form {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          padding: 32px;
          border-radius: 10px;
          width: 100%;
          max-width: 380px;
        }
        .login-logo {
          font-family: var(--font-mono);
          font-size: 13px;
          font-weight: 600;
          letter-spacing: .15em;
          color: var(--color-accent);
        }
        .login-form h2 {
          margin: 16px 0 4px;
          font-size: 18px;
          color: var(--color-text);
        }
        .login-sub {
          color: var(--color-muted);
          font-size: 12px;
          margin: 0 0 24px;
        }
        .login-modes {
          display: flex;
          gap: 8px;
          margin-bottom: 20px;
        }
        .login-mode {
          flex: 1;
          padding: 8px;
          background: var(--color-surface-2);
          color: var(--color-muted);
          border: 1px solid var(--color-border);
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
        }
        /* El tab activo se distingue por fondo de acento, no solo por el borde:
           sobre el tema claro el borde suelto no daba contraste suficiente. */
        .login-mode.active {
          color: var(--color-accent);
          background: var(--accent-soft);
          border-color: var(--color-accent);
          font-weight: 600;
        }
        .form-group { margin-bottom: 16px; }
        .form-group label {
          display: block;
          margin-bottom: 6px;
          color: var(--color-muted);
          font-size: 12px;
        }
        .form-group input {
          width: 100%;
          padding: 10px 12px;
          background: var(--color-surface-2);
          border: 1px solid var(--color-border);
          border-radius: 6px;
          color: var(--color-text);
          font-size: 14px;
          box-sizing: border-box;
        }
        .form-group input:focus {
          outline: none;
          border-color: var(--color-accent);
        }
        .login-submit {
          width: 100%;
          padding: 10px;
          margin-top: 4px;
          background: var(--color-accent);
          color: #0b1220;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
        }
        .login-submit:disabled { opacity: .5; cursor: default; }
        .login-msg {
          padding: 10px 12px;
          border-radius: 6px;
          margin-bottom: 16px;
          font-size: 12px;
        }
        .login-msg.error {
          background: rgba(248, 113, 113, .12);
          color: var(--color-danger);
          border: 1px solid rgba(248, 113, 113, .3);
        }
        .login-msg.notice {
          background: rgba(52, 211, 153, .12);
          color: var(--color-success);
          border: 1px solid rgba(52, 211, 153, .3);
        }
      `}</style>
    </div>
  );
};
